import { createSlice } from "@reduxjs/toolkit";
import { deleteBagItem, getBagItems, getDriverBagAnalytics, getDriverBagSales, postbagWithItem, getTransferBagItems, transferDriverBag, getReturnBagPreview, returnDriverBag, getDriverBagHistory, getDriverBagReconciliation, getBagAssignmentReconciliation, getBagAssignmentDetail, getBagAssignments, getPendingBagTransfers, adminConfirmPendingTransfer, adminCancelPendingTransfer, transferBagToSubmenu, refillBagFromSubmenu, getBagToSubmenuPreview } from "./bagItemsActions";

const initialState = {
  loading: false,
  error: null,
  success: false,
  items: {},
  sales: null,
  salesLoading: false,
  analytics: null,
  analyticsLoading: false,
  transferItems: null,
  transferItemsLoading: false,
  transferLoading: false,
  transferSuccess: false,
  returnLoading: false,
  returnSuccess: false,
  history: null,
  historyLoading: false,
  reconciliation: null,
  reconciliationLoading: false,
  bagAssignmentDetail: null,
  bagAssignmentDetailLoading: false,
  returnPreview: null,
  returnPreviewLoading: false,
  returnPreviewError: null,
  assignments: null,
  assignmentsLoading: false,
  pendingTransfersList: {
    items: [],
    meta: null,
    loading: false,
    error: null,
  },
  pendingTransferActionId: null,
  pendingTransferActionType: null,
  pendingTransferError: null,
  submenuTransferLoading: false,
  submenuTransferSuccess: false,
  refillLoading: false,
  refillSuccess: false,
  submenuActionError: null,
  submenuTransferPreview: null,
  submenuTransferPreviewLoading: false,
};

const bagItemSlice = createSlice({
  name: "bagItems",
  initialState,
  reducers: {
    resetItems: (state) => {
      state.items = {};
      state.error = null;
      state.success = false;
      state.loading = false;
    },
    resetTransferReturn: (state) => {
      state.transferItems = null;
      state.transferItemsLoading = false;
      state.transferLoading = false;
      state.transferSuccess = false;
      state.returnLoading = false;
      state.returnSuccess = false;
      state.returnPreview = null;
      state.returnPreviewLoading = false;
      state.returnPreviewError = null;
    },
    resetBagsItemsError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    resetPendingTransferError: (state) => {
      state.pendingTransferError = null;
    },
    resetSubmenuActions: (state) => {
      state.submenuTransferLoading = false;
      state.submenuTransferSuccess = false;
      state.refillLoading = false;
      state.refillSuccess = false;
      state.submenuActionError = null;
      state.submenuTransferPreview = null;
      state.submenuTransferPreviewLoading = false;
    },
    // Clears only the submenu action error — preserves the loaded preview so a
    // failed transfer doesn't wipe the items table.
    resetSubmenuActionError: (state) => {
      state.submenuActionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBagItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBagItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getBagItems.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(deleteBagItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBagItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = {
          meta: state.items.meta,
          results: state.items.results.filter(
            (el) => el.id !== action.payload.id
          ),
        };
      })
      .addCase(deleteBagItem.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(postbagWithItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(postbagWithItem.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(postbagWithItem.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.success = false;
      })
      .addCase(getDriverBagSales.pending, (state) => {
        state.salesLoading = true;
      })
      .addCase(getDriverBagSales.fulfilled, (state, action) => {
        state.salesLoading = false;
        state.sales = action.payload;
      })
      .addCase(getDriverBagSales.rejected, (state, action) => {
        state.salesLoading = false;
        state.sales = null;
      })
      .addCase(getDriverBagAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(getDriverBagAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
      })
      .addCase(getDriverBagAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = null;
      })
      .addCase(getTransferBagItems.pending, (state) => {
        state.transferItemsLoading = true;
      })
      .addCase(getTransferBagItems.fulfilled, (state, action) => {
        state.transferItemsLoading = false;
        state.transferItems = action.payload;
      })
      .addCase(getTransferBagItems.rejected, (state, action) => {
        state.transferItemsLoading = false;
        state.transferItems = null;
      })
      .addCase(transferDriverBag.pending, (state) => {
        state.transferLoading = true;
        state.transferSuccess = false;
      })
      .addCase(transferDriverBag.fulfilled, (state) => {
        state.transferLoading = false;
        state.transferSuccess = true;
      })
      .addCase(transferDriverBag.rejected, (state, action) => {
        state.transferLoading = false;
        state.transferSuccess = false;
        state.error = action.payload;
      })
      .addCase(getReturnBagPreview.pending, (state) => {
        state.returnPreviewLoading = true;
        state.returnPreviewError = null;
      })
      .addCase(getReturnBagPreview.fulfilled, (state, action) => {
        state.returnPreviewLoading = false;
        state.returnPreview = action.payload;
        state.returnPreviewError = null;
      })
      .addCase(getReturnBagPreview.rejected, (state, action) => {
        state.returnPreviewLoading = false;
        state.returnPreview = null;
        state.returnPreviewError = action.payload;
      })
      .addCase(returnDriverBag.pending, (state) => {
        state.returnLoading = true;
        state.returnSuccess = false;
      })
      .addCase(returnDriverBag.fulfilled, (state) => {
        state.returnLoading = false;
        state.returnSuccess = true;
      })
      .addCase(returnDriverBag.rejected, (state, action) => {
        state.returnLoading = false;
        state.returnSuccess = false;
        state.error = action.payload;
      })
      .addCase(getDriverBagHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(getDriverBagHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload;
      })
      .addCase(getDriverBagHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.history = null;
      })
      .addCase(getDriverBagReconciliation.pending, (state) => {
        state.reconciliationLoading = true;
      })
      .addCase(getDriverBagReconciliation.fulfilled, (state, action) => {
        state.reconciliationLoading = false;
        state.reconciliation = action.payload;
      })
      .addCase(getDriverBagReconciliation.rejected, (state, action) => {
        state.reconciliationLoading = false;
        state.reconciliation = null;
      })
      .addCase(getBagAssignmentReconciliation.pending, (state) => {
        state.reconciliationLoading = true;
      })
      .addCase(getBagAssignmentReconciliation.fulfilled, (state, action) => {
        state.reconciliationLoading = false;
        state.reconciliation = action.payload;
      })
      .addCase(getBagAssignmentReconciliation.rejected, (state, action) => {
        state.reconciliationLoading = false;
        state.reconciliation = null;
      })
      .addCase(getBagAssignmentDetail.pending, (state) => {
        state.bagAssignmentDetailLoading = true;
      })
      .addCase(getBagAssignmentDetail.fulfilled, (state, action) => {
        state.bagAssignmentDetailLoading = false;
        state.bagAssignmentDetail = action.payload;
      })
      .addCase(getBagAssignmentDetail.rejected, (state, action) => {
        state.bagAssignmentDetailLoading = false;
        state.bagAssignmentDetail = null;
      })
      .addCase(getBagAssignments.pending, (state) => {
        state.assignmentsLoading = true;
      })
      .addCase(getBagAssignments.fulfilled, (state, action) => {
        state.assignmentsLoading = false;
        state.assignments = action.payload;
      })
      .addCase(getBagAssignments.rejected, (state, action) => {
        state.assignmentsLoading = false;
        state.assignments = null;
      })
      .addCase(getPendingBagTransfers.pending, (state) => {
        state.pendingTransfersList.loading = true;
        state.pendingTransfersList.error = null;
      })
      .addCase(getPendingBagTransfers.fulfilled, (state, action) => {
        state.pendingTransfersList.loading = false;
        state.pendingTransfersList.items = action.payload?.results || [];
        state.pendingTransfersList.meta = action.payload?.meta || null;
        state.pendingTransfersList.error = null;
      })
      .addCase(getPendingBagTransfers.rejected, (state, action) => {
        state.pendingTransfersList.loading = false;
        state.pendingTransfersList.error = action.payload;
      })
      .addCase(adminConfirmPendingTransfer.pending, (state, action) => {
        state.pendingTransferActionId = action.meta.arg?.id;
        state.pendingTransferActionType = "confirm";
        state.pendingTransferError = null;
      })
      .addCase(adminConfirmPendingTransfer.fulfilled, (state) => {
        state.pendingTransferActionId = null;
        state.pendingTransferActionType = null;
      })
      .addCase(adminConfirmPendingTransfer.rejected, (state, action) => {
        state.pendingTransferActionId = null;
        state.pendingTransferActionType = null;
        state.pendingTransferError = action.payload;
      })
      .addCase(adminCancelPendingTransfer.pending, (state, action) => {
        state.pendingTransferActionId = action.meta.arg?.id;
        state.pendingTransferActionType = "cancel";
        state.pendingTransferError = null;
      })
      .addCase(adminCancelPendingTransfer.fulfilled, (state) => {
        state.pendingTransferActionId = null;
        state.pendingTransferActionType = null;
      })
      .addCase(adminCancelPendingTransfer.rejected, (state, action) => {
        state.pendingTransferActionId = null;
        state.pendingTransferActionType = null;
        state.pendingTransferError = action.payload;
      })
      .addCase(transferBagToSubmenu.pending, (state) => {
        state.submenuTransferLoading = true;
        state.submenuTransferSuccess = false;
        state.submenuActionError = null;
      })
      .addCase(transferBagToSubmenu.fulfilled, (state) => {
        state.submenuTransferLoading = false;
        state.submenuTransferSuccess = true;
      })
      .addCase(transferBagToSubmenu.rejected, (state, action) => {
        state.submenuTransferLoading = false;
        state.submenuTransferSuccess = false;
        state.submenuActionError = action.payload;
      })
      .addCase(refillBagFromSubmenu.pending, (state) => {
        state.refillLoading = true;
        state.refillSuccess = false;
        state.submenuActionError = null;
      })
      .addCase(refillBagFromSubmenu.fulfilled, (state) => {
        state.refillLoading = false;
        state.refillSuccess = true;
      })
      .addCase(refillBagFromSubmenu.rejected, (state, action) => {
        state.refillLoading = false;
        state.refillSuccess = false;
        state.submenuActionError = action.payload;
      })
      .addCase(getBagToSubmenuPreview.pending, (state) => {
        state.submenuTransferPreviewLoading = true;
      })
      .addCase(getBagToSubmenuPreview.fulfilled, (state, action) => {
        state.submenuTransferPreviewLoading = false;
        state.submenuTransferPreview = action.payload;
      })
      .addCase(getBagToSubmenuPreview.rejected, (state) => {
        state.submenuTransferPreviewLoading = false;
        state.submenuTransferPreview = null;
      })
;
  },
});
export const { resetItems, resetBagsItemsError, resetTransferReturn, resetPendingTransferError, resetSubmenuActions, resetSubmenuActionError } = bagItemSlice.actions;
export default bagItemSlice.reducer;
